import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./dialog";
import { Input } from "./input";
import { Label } from "./label";

interface LinkInputDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (url: string) => void;
    initialUrl?: string;
}

const LinkInputDialog = ({
    isOpen,
    onClose,
    onConfirm,
    initialUrl = "",
}: LinkInputDialogProps) => {
    const { t } = useTranslation();
    const [url, setUrl] = useState(initialUrl);

    const handleConfirm = () => {
        if (url.trim()) {
            // Ensure URL has protocol
            let finalUrl = url.trim();
            if (
                !finalUrl.startsWith("http://") &&
                !finalUrl.startsWith("https://")
            ) {
                finalUrl = `https://${finalUrl}`;
            }
            onConfirm(finalUrl);
            setUrl("");
        }
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleConfirm();
        } else if (e.key === "Escape") {
            onClose();
        }
    };

    return (
        <Dialog onOpenChange={onClose} open={isOpen}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{t("richTextEditor.addLink")}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-col items-center gap-4">
                    <div className="flex w-full items-center gap-2">
                        <Label htmlFor="url">{t("richTextEditor.url")}</Label>
                        <Input
                            className="col-span-3"
                            id="url"
                            onChange={(e) => setUrl(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t("richTextEditor.urlPlaceholder")}
                            value={url}
                            autoFocus
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={onClose} variant="outline">
                        {t("common.cancel")}
                    </Button>
                    <Button disabled={!url.trim()} onClick={handleConfirm}>
                        {t("common.add")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default LinkInputDialog;
